/**
 * Slicing a series table for a chart (specs/workshop-modules/statistics_view.md → Slicing).
 * A choice fixes every dimension to one value or spreads it as the series;
 * at most two are spread. Pure functions, no React.
 */

export const MAX_SERIES = 8;
export const MAX_PANELS = 6;

/** The default choice for a table: the first dimension spread, the rest fixed to their first value. */
export function defaultChoice(table) {
  const dims = table?.dimensions || [];
  const spread = dims.length ? [dims[0].key] : [];
  const fixed = {};
  for (const d of dims.slice(1)) fixed[d.key] = d.values[0];
  const periods = table?.periods || [];
  return {
    tableId: table?.id,
    spread,
    fixed,
    chart: periods.length > 1 ? 'line' : 'bars',
    period: periods.length ? periods[periods.length - 1] : null,
  };
}

/** A choice brought in line with its table: unknown dimensions dropped, every other one fixed. */
export function normaliseChoice(table, choice) {
  const base = defaultChoice(table);
  if (!choice || choice.tableId !== table?.id) return base;
  const dims = table.dimensions || [];
  const keys = new Set(dims.map((d) => d.key));
  const spread = (choice.spread || []).filter((k) => keys.has(k)).slice(0, 2);
  const fixed = {};
  for (const d of dims) {
    if (spread.includes(d.key)) continue;
    const v = choice.fixed?.[d.key];
    fixed[d.key] = d.values.includes(v) ? v : d.values[0];
  }
  const chart = choice.chart === 'bars' ? 'bars' : 'line';
  const period = (table.periods || []).includes(choice.period) ? choice.period : base.period;
  return { tableId: table.id, spread, fixed, chart, period };
}

const dimValues = (table, key) => table.dimensions.find((d) => d.key === key)?.values || [];

/**
 * The chart's data. Values are the table's rows already filtered to the
 * fixed dimensions (the API does that). Returns
 *   { panels: [{ key, label, series: [{ key, label, points: [{ x, value, flag }] }], xs }],
 *     truncated: { shown, total } | null }
 * A line chart's x is the period; a bar chart's x is the first spread
 * dimension's values at one period, with the second spread dimension as series.
 */
export function sliceValues(table, values, choice) {
  const [s1, s2] = choice.spread;
  const rows = values.filter((r) => Object.entries(choice.fixed).every(([k, v]) => r.dims[k] === v));
  const all1 = s1 ? dimValues(table, s1) : [];
  const all2 = s2 ? dimValues(table, s2) : [];

  if (choice.chart === 'bars') {
    const period = choice.period || table.periods[table.periods.length - 1];
    const atPeriod = rows.filter((r) => r.period === period);
    // Only the categories the slice has a row for: fixing one dimension (a state, a group)
    // must not leave the bar chart a slot for every value the table knows.
    const xs = s1 ? all1.filter((x) => atPeriod.some((r) => r.dims[s1] === x)) : [table.title];
    const seriesKeys = s2 ? all2.slice(0, MAX_SERIES) : [null];
    const series = seriesKeys.map((sk) => ({
      key: sk ?? 'value',
      label: sk ?? table.title,
      points: xs.map((x) => {
        const r = atPeriod.find((row) => (!s1 || row.dims[s1] === x) && (!s2 || row.dims[s2] === sk));
        return { x, value: r?.value ?? null, flag: r?.flag ?? null };
      }),
    }));
    return {
      panels: [{ key: period, label: period, xs, series }],
      truncated: s2 && all2.length > MAX_SERIES ? { shown: MAX_SERIES, total: all2.length } : null,
    };
  }

  const xs = table.periods;
  const panelKeys = s2 ? all2.slice(0, MAX_PANELS) : [null];
  const seriesKeys = s1 ? all1.slice(0, MAX_SERIES) : [null];
  const panels = panelKeys.map((pk) => ({
    key: pk ?? 'all',
    label: pk ?? '',
    xs,
    series: seriesKeys.map((sk) => ({
      key: sk ?? 'value',
      label: sk ?? table.title,
      points: xs.map((x) => {
        const r = rows.find((row) => row.period === x && (!s1 || row.dims[s1] === sk) && (!s2 || row.dims[s2] === pk));
        return { x, value: r?.value ?? null, flag: r?.flag ?? null };
      }),
    })),
  }));
  const truncated = s1 && all1.length > MAX_SERIES ? { shown: MAX_SERIES, total: all1.length } : null;
  return { panels, truncated };
}
